/**
 * Site-wide Breaking News ticker. Mounts globally at the top of every
 * NewsPulse page. Polls `/events/ticker` every 60s, cycles headlines
 * on a horizontal marquee, and links each item to the event detail page.
 *
 * Silent when there are no breaking events — no space is reserved so the
 * layout doesn't jump.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, X } from "lucide-react";
import { npApi, hasNpAuthToken } from "./api";

const POLL_MS = 60_000;
const DISMISS_KEY = "np.ticker.dismissed_at";
const DISMISS_TTL_MS = 30 * 60 * 1000; // stay hidden for 30 min after dismiss

export default function BreakingTicker() {
  const [items, setItems] = useState([]);
  const [prefsSlugs, setPrefsSlugs] = useState(null); // null = unknown yet
  const [dismissed, setDismissed] = useState(() => {
    try {
      const ts = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
      return ts > 0 && Date.now() - ts < DISMISS_TTL_MS;
    } catch {
      return false;
    }
  });
  const timerRef = useRef(null);

  // Fetch the signed-in reader's pinned sections + locations once. If
  // no token or the request fails we leave prefsSlugs = null and the
  // ticker falls back to global breaking news.
  useEffect(() => {
    let alive = true;
    async function loadPrefs() {
      try {
        if (!hasNpAuthToken()) return;
        const p = await npApi.prefsGet();
        if (!alive || !p) return;
        const sections = Array.isArray(p.pinned_sections) ? p.pinned_sections : [];
        const locations = Array.isArray(p.pinned_locations) ? p.pinned_locations : [];
        setPrefsSlugs({ sections, locations });
      } catch {
        /* anon or empty prefs — ignore, keep global feed */
      }
    }
    loadPrefs();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const data = await npApi.ticker(20);
        if (alive) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setItems([]);
      }
    }
    load();
    timerRef.current = window.setInterval(load, POLL_MS);
    return () => {
      alive = false;
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  // Personalized filter: when the reader has pinned sections or
  // locations, keep only tickers whose primary_category prefix matches
  // a pinned section OR whose headline / neighborhood contains a
  // pinned location. If the filter drops everything we fall back to
  // the raw global list so the ticker never goes dark on breaking news.
  const filteredItems = useMemo(() => {
    if (!prefsSlugs || (!prefsSlugs.sections.length && !prefsSlugs.locations.length)) {
      return items;
    }
    const secSet = new Set(prefsSlugs.sections);
    const locRegex = prefsSlugs.locations.length
      ? new RegExp(prefsSlugs.locations
          .filter((l) => l && l.length >= 3 && l.length <= 60)
          .map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
          .join("|"), "i")
      : null;
    const kept = items.filter((ev) => {
      const primary = (ev.primary_category || "").split("/")[0];
      if (primary && secSet.has(primary)) return true;
      if ((ev.categories || []).some((c) => secSet.has(c.split("/")[0]))) return true;
      if (locRegex) {
        if (ev.neighborhood && locRegex.test(ev.neighborhood)) return true;
        if (ev.headline && locRegex.test(ev.headline)) return true;
      }
      return false;
    });
    // Fallback: never render an empty personalized ticker on breaking news.
    return kept.length ? kept.slice(0, 8) : items.slice(0, 8);
  }, [items, prefsSlugs]);

  const marqueeItems = useMemo(() => {
    return filteredItems.length ? [...filteredItems, ...filteredItems] : [];
  }, [filteredItems]);

  const isPersonalized =
    !!prefsSlugs && (prefsSlugs.sections.length > 0 || prefsSlugs.locations.length > 0);

  const onDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  if (dismissed || !items.length) return null;

  return (
    <div
      className="bg-red-600 text-white overflow-hidden border-b border-red-700"
      data-testid="newspulse-breaking-ticker"
    >
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 flex items-center gap-3 h-9">
        <div
          className="flex items-center gap-1.5 shrink-0 pr-3 border-r border-red-400/60"
          data-testid="ticker-label"
        >
          <Radio className="w-3.5 h-3.5 animate-pulse" />
          <span className="text-[11px] font-bold uppercase tracking-widest">
            Breaking
          </span>
          {isPersonalized && (
            <span
              className="text-[9px] font-bold uppercase tracking-widest bg-white/20 rounded-full px-1.5 py-0.5"
              data-testid="ticker-personalized-badge"
              title="Filtered to your pinned sections and locations"
            >
              For You
            </span>
          )}
        </div>

        <div className="relative flex-1 overflow-hidden">
          <div
            className="flex items-center gap-8 whitespace-nowrap animate-[np-marquee_60s_linear_infinite]"
            data-testid="ticker-track"
          >
            {marqueeItems.map((ev, i) => (
              <Link
                key={`${ev.event_id}-${i}`}
                to={`/event/${ev.event_id}`}
                className="text-sm hover:underline decoration-white/60 underline-offset-2"
                data-testid={`ticker-item-${i}`}
              >
                <span className="opacity-80 mr-2">•</span>
                {ev.headline}
              </Link>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss breaking ticker"
          className="shrink-0 w-6 h-6 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
          data-testid="ticker-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <style>{`
        @keyframes np-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
